import {
  EmptyStatePage,
  GlassCard,
  Glyph,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const CARD_SHADOW =
  "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.28),inset_0_-1px_0_0_rgb(255_255_255/0.06),0_18px_44px_-16px_rgb(0_0_0/0.65)]";

const BOX_ICON =
  "M5.52 2.64 3.96 4.72c-.309.412-.463.618-.46.79a.5.5 0 0 0 .192.384C3.828 6 4.085 6 4.6 6h14.8c.515 0 .773 0 .908-.106a.5.5 0 0 0 .192-.384c.003-.172-.151-.378-.46-.79l-1.56-2.08m-12.96 0c.176-.235.264-.352.376-.437a1 1 0 0 1 .33-.165C6.36 2 6.505 2 6.8 2h10.4c.293 0 .44 0 .575.038a1 1 0 0 1 .33.165c.111.085.199.202.375.437m-12.96 0L3.64 5.147c-.237.316-.356.475-.44.649a2 2 0 0 0-.163.487C3 6.473 3 6.671 3 7.067V18.8c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C4.52 22 5.08 22 6.2 22h11.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C21 20.48 21 19.92 21 18.8V7.067c0-.396 0-.594-.037-.784a1.998 1.998 0 0 0-.163-.487c-.084-.174-.203-.333-.44-.65L18.48 2.64M16 10a4 4 0 1 1-8 0";
const TREND_UP_ICON =
  "m22 7-7.869 7.869c-.396.396-.594.594-.822.668a1 1 0 0 1-.618 0c-.228-.074-.426-.272-.822-.668L9.13 12.13c-.396-.396-.594-.594-.822-.668a1 1 0 0 0-.618 0c-.228.074-.426.272-.822.668L2 17M22 7h-7m7 0v7";

const PRODUCTS: { name: string; rank: string; trend: string; gmv: string; delay: number }[] = [
  { name: "Hydrating Serum 30ml", rank: "#1 this month", trend: "+34%", gmv: "£8.4K", delay: 0.55 },
  { name: "Matte Lip Set", rank: "#2 this month", trend: "+12%", gmv: "£5.1K", delay: 0.68 },
  { name: "Cleansing Balm", rank: "#3 this month", trend: "+6%", gmv: "£2.7K", delay: 0.81 },
];

type AllProductsEmptyStateProps = {
  onAddProducts: () => void;
};

export function AllProductsEmptyState({ onAddProducts }: AllProductsEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);

  return (
    <EmptyStatePage
      eyebrow="All Products"
      headline="Get your first products listed"
      subline="Add products in TikTok Shop and your whole catalogue lands here, with GMV, orders and creator coverage on every one. You’ll see what’s earning and what’s sitting idle."
      measureCh={46}
      portraitSrc="/empty-states/portrait-all-products.webp"
      portraitMaskLeft="34%"
      portraitMaskBottom="78%"
      portraitClassName="top-1/2 -right-20 h-auto w-[58%] -translate-y-1/2 opacity-55"
      actionsDelaySeconds={1.16}
      primaryAction={{ label: "Add products on TikTok", onClick: onAddProducts }}
      device={
        <div className="w-full max-w-[560px]">
          <GlassCard
            radius="2xl"
            className={`w-full p-6 backdrop-blur-3xl ${CARD_SHADOW}`}
            style={{ animation: entrance.glassIn(0.3) }}
            overlayStyle={{ animation: entrance.fadeIn(0.3) }}
          >
            <div className="flex items-center justify-between gap-4" style={{ animation: entrance.fadeInUp(0.3) }}>
              <p className="text-base leading-6 text-white">Your catalogue, ranked by{" "}GMV</p>
              <span aria-hidden="true" className="shrink-0 text-white/70">
                <Glyph d={BOX_ICON} />
              </span>
            </div>
            <div className="mt-5 flex flex-col">
              {PRODUCTS.map((product) => (
                <div
                  key={product.name}
                  className="flex items-center gap-4 border-t border-white/12 py-3 first:border-t-0 first:pt-0"
                  style={{ animation: reduceMotion ? undefined : `fade-in-up 0.44s ease-out ${product.delay}s both` }}
                >
                  <div className="flex min-w-0 flex-col">
                    <p className="truncate text-sm font-semibold text-white">{product.name}</p>
                    <p className="text-xs text-brand-100">{product.rank}</p>
                  </div>
                  <div className="ml-auto flex shrink-0 items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-200">
                      <Glyph d={TREND_UP_ICON} size={14} />
                      {product.trend}
                    </span>
                    <span className="text-sm font-semibold text-white">{product.gmv}</span>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      }
    />
  );
}
